import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Landmark, Plus, Pencil, Trash2, Loader2, AlertTriangle, CheckCircle2, Wallet, PiggyBank,
} from "lucide-react";
import { toast } from "sonner";
import { InfoHint } from "@/components/InfoHint";

const emptyForm = { name: "", bank: "", balance: "", kind: "current" };

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
};

export function BankCashFlow({ year, month, refreshKey }) {
  const [accounts, setAccounts] = useState([]);
  const [cf, setCf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/bank-accounts").then((r) => setAccounts(r.data));
    api.get(`/cash-flow?year=${year}&month=${month}`).then((r) => setCf(r.data)).finally(() => setLoading(false));
  }, [year, month, refreshKey]);
  useEffect(load, [load]);

  const openNew = () => { setForm(emptyForm); setEditId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ name: a.name, bank: a.bank || "", balance: String(a.balance), kind: "current" }); setEditId(a.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom du compte requis"); return; }
    setSaving(true);
    try {
      const bal = Number(form.balance) || 0;
      if (form.kind === "savings") {
        // Compte épargne -> collection savings_accounts (visible dans l'onglet Épargne, hors trésorerie)
        await api.post("/savings/accounts", { name: form.name.trim(), type: "cash", balance: bal, institution: form.bank.trim(), color: "#22293A" });
        if (editId) await api.delete(`/bank-accounts/${editId}`);
        toast.success(editId ? "Compte déplacé vers l'Épargne" : "Compte épargne créé (visible dans l'onglet Épargne)");
      } else {
        const payload = { name: form.name.trim(), bank: form.bank.trim(), balance: bal };
        if (editId) await api.put(`/bank-accounts/${editId}`, payload);
        else await api.post("/bank-accounts", payload);
        toast.success(editId ? "Compte mis à jour" : "Compte courant ajouté");
      }
      setOpen(false); load();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };
  const remove = async (id) => { await api.delete(`/bank-accounts/${id}`); toast.success("Compte supprimé"); load(); };

  if (loading || !cf) {
    return <Card className="p-8 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></Card>;
  }

  const suff = cf.sufficient;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="bank-cashflow">
      {/* Comptes en banque */}
      <Card className="p-5 border-border" data-testid="bank-accounts-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Landmark className="w-4 h-4 text-primary" />
            <h3 className="font-display font-semibold text-base">Comptes en banque</h3>
          </div>
          <Button variant="outline" size="sm" onClick={openNew} data-testid="add-bank-account-button">
            <Plus className="w-4 h-4 mr-1.5" />Ajouter
          </Button>
        </div>
        {accounts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            Aucun compte. Ajoutez vos comptes et leur solde (visible sur vos relevés) pour suivre votre trésorerie.
          </p>
        ) : (
          <div className="space-y-2">
            {accounts.map((a) => (
              <div key={a.id} className="flex items-center gap-3 py-2 border-b border-border/60 last:border-0" data-testid={`bank-account-row-${a.id}`}>
                <span className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Wallet className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{a.name}</div>
                  {a.bank && <div className="text-xs text-muted-foreground truncate">{a.bank}</div>}
                </div>
                <div className="text-right">
                  <div className="font-num text-sm font-medium">{fmtEuro(a.balance)}</div>
                  {a.updated_at && <div className="text-[10px] text-muted-foreground" data-testid={`bank-account-updated-${a.id}`}>Maj {fmtDate(a.updated_at)}</div>}
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(a)} data-testid={`edit-bank-account-${a.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(a.id)} data-testid={`delete-bank-account-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <div className="flex items-center justify-between pt-3 mt-1 border-t border-border">
              <span className="text-sm font-semibold">Total disponible</span>
              <span className="font-num text-lg font-semibold" data-testid="bank-total">{fmtEuro(cf.bank_total)}</span>
            </div>
          </div>
        )}
      </Card>

      {/* Trésorerie fin de mois */}
      <Card className="p-5 border-border" data-testid="cashflow-card">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-display font-semibold text-base">Trésorerie fin de mois prévisionnel</h3>
          <InfoHint testid="cashflow-title-info" text="Estimation du solde de vos comptes courants à la fin du mois sélectionné, une fois toutes les dépenses et rentrées prévues (mensuelles + exceptionnelles) prises en compte." />
        </div>
        <p className="text-xs text-muted-foreground mb-4">Solde projeté une fois toutes les dépenses et rentrées prévues du mois passées.</p>

        <div className="rounded-lg bg-secondary/50 p-4 mb-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Solde projeté en fin de mois</div>
          <div className={`font-num text-3xl font-semibold mt-1 ${suff ? "text-emerald-600" : "text-destructive"}`} data-testid="projected-balance">
            {fmtEuro(cf.projected_balance)}
          </div>
        </div>

        {suff ? (
          <div className="flex items-start gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50 p-3 mb-4" data-testid="cashflow-ok">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-800">Cash suffisant pour finir le mois.</div>
          </div>
        ) : (
          <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 p-3 mb-4" data-testid="cashflow-warning">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <span className="font-semibold">Attention : cash insuffisant</span> pour finir le mois. Il manque {fmtEuro(Math.abs(cf.projected_balance))} : <span className="font-medium">{fmtEuro(cf.shortfall_courant || 0)}</span> dû à vos dépenses courantes et <span className="font-medium">{fmtEuro(cf.shortfall_exceptionnel || 0)}</span> dû à vos dépenses exceptionnelles.
            </div>
          </div>
        )}

        <div className="space-y-1.5 text-sm">
          <Row label="Solde en banque" value={cf.bank_effective != null ? cf.bank_effective : cf.bank_total} info={cf.is_future ? "Mois futur : le solde actuel n'est pas reporté, le point de départ est 0 € (la projection ne tient compte que des rentrées et dépenses du mois)." : "Somme des soldes actuels de vos comptes courants (le total disponible affiché à gauche)."} />
          <Row label="Dépenses mensuelles déjà faites" value={-(cf.spent || 0)} muted info="Dépenses de vos catégories mensuelles déjà enregistrées ce mois-ci, hors provisions exceptionnelles liées." />
          <Row label="Dépenses mensuelles restantes" value={-cf.remaining_expenses} muted info="Budget mensuel prévu (mois type + ajustements) encore non dépensé = budget − déjà dépensé." />
          <Row label="Dépenses exceptionnelles faites" value={-(cf.exceptional_spent || 0)} muted info="Provisions de la Vue Annuelle déjà payées ce mois (mouvements rattachés dans le cadre jaune)." />
          <Row label="Dépenses exceptionnelles à venir" value={-cf.exceptional_expense} muted info="Provisions exceptionnelles prévues ce mois (Vue Annuelle) pas encore payées." />
          <Row label="Rentrées mensuelles à venir" value={cf.remaining_income} muted info="Revenus mensuels prévus (mois type) encore non reçus = prévu − déjà reçu." />
          <Row label="Rentrées exceptionnelles à venir" value={cf.exceptional_income} muted info="Rentrées exceptionnelles prévues ce mois (Vue Annuelle) pas encore reçues." />
          <div className="flex justify-between pt-2 mt-1 border-t border-border font-semibold">
            <span>Solde projeté</span>
            <span className={`font-num ${suff ? "text-emerald-600" : "text-destructive"}`}>{fmtEuro(cf.projected_balance)}</span>
          </div>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="bank-account-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Modifier le compte" : "Nouveau compte"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Type de compte</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="bank-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="current"><span className="flex items-center gap-2"><Landmark className="w-3.5 h-3.5" />Compte courant</span></SelectItem>
                  <SelectItem value="savings"><span className="flex items-center gap-2"><PiggyBank className="w-3.5 h-3.5" />Compte épargne</span></SelectItem>
                </SelectContent>
              </Select>
              {form.kind === "savings" && (
                <p className="text-[11px] text-muted-foreground">{editId ? "Ce compte sera déplacé vers l'onglet Épargne et retiré du calcul de trésorerie." : "Ce compte apparaîtra dans l'onglet Épargne et n'entrera pas dans le calcul de trésorerie."}</p>
              )}
            </div>
            <div className="space-y-1.5"><Label>Nom du compte</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Compte à vue commun" data-testid="bank-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Banque (optionnel)</Label><Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} placeholder="Ex: Belfius, CBC" data-testid="bank-institution-input" /></div>
              <div className="space-y-1.5"><Label>Solde actuel (€)</Label><Input type="number" step="0.01" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} placeholder="0,00" data-testid="bank-balance-input" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="save-bank-account-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, muted, info }) {
  const positive = value >= 0;
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground flex items-center gap-1.5">{label}{info && <InfoHint text={info} />}</span>
      <span className={`font-num ${positive ? "text-emerald-600" : "text-foreground"}`}>
        {positive ? "+" : "−"}{fmtEuro(Math.abs(value))}
      </span>
    </div>
  );
}
